import { useState, useRef, useEffect, useMemo } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Send, Paperclip, Reply, Smile, Pencil, Trash2, X, MessageCirclePlus, ArrowLeft, AtSign } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { timeAgo } from "@/lib/constants";
import { SpaceSection } from "@/components/SpaceSection";
import { UserAvatar } from "@/components/Avatars";
import { downloadUrl, fmtSize, CategoryIcon } from "@/pages/FilesPage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { WeeklyReportCard } from "@/components/WeeklyReport";

const EMOJIS = ["👍", "❤️", "😂", "🎉", "👏", "😮"];

const MessageBubble = ({ msg, isMine, isAdmin, onReply, onEdit, onDelete, onReact }) => {
  if (msg.deleted) {
    return <p className="text-xs italic text-muted-foreground px-11 py-1">Mensaje eliminado</p>;
  }
  const editable = isMine && (Date.now() - new Date(msg.created_at).getTime()) < 15 * 60 * 1000;
  return (
    <div className="flex gap-2.5 px-3 py-1.5 hover:bg-secondary/40 group rounded-lg" data-testid={`message-${msg.id}`}>
      <UserAvatar name={msg.author_name} color={msg.author_color} size="sm" className="mt-0.5" />
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm font-semibold">{msg.author_name}</span>
          <span className="text-[10px] text-muted-foreground">{timeAgo(msg.created_at)}</span>
          {msg.edited_at && <span className="text-[10px] text-muted-foreground">(editado)</span>}
        </div>
        {msg.reply_to && (
          <div className="border-l-2 border-primary/40 pl-2 my-1 text-xs text-muted-foreground truncate">
            <span className="font-medium">{msg.reply_to.author_name}:</span> {msg.reply_to.text}
          </div>
        )}
        {msg.text && <p className="text-sm whitespace-pre-wrap break-words">{msg.text}</p>}
        {msg.meta?.weekly_report && <WeeklyReportCard report={msg.meta.weekly_report} />}
        {msg.files?.map((f) => (
          f.category === "image" ? (
            <img key={f.id} src={downloadUrl(f.id)} alt={f.name} className="max-h-48 rounded-lg mt-1.5" loading="lazy" />
          ) : (
            <a key={f.id} href={downloadUrl(f.id)} download={f.name}
              className="flex items-center gap-2 border rounded-lg px-3 py-2 mt-1.5 w-fit text-xs hover:bg-secondary">
              <CategoryIcon category={f.category} className="h-4 w-4 text-muted-foreground" />
              <span className="font-medium">{f.name}</span>
              <span className="text-muted-foreground">{fmtSize(f.size)}</span>
            </a>
          )
        ))}
        {Object.keys(msg.reactions || {}).length > 0 && (
          <div className="flex gap-1 mt-1 flex-wrap">
            {Object.entries(msg.reactions).map(([emoji, users]) => (
              <button key={emoji} onClick={() => onReact(msg, emoji)}
                data-testid={`reaction-chip-${msg.id}-${emoji}`}
                className="text-xs bg-secondary rounded-full px-2 py-0.5 hover:bg-accent">
                {emoji} {users.length}
              </button>
            ))}
          </div>
        )}
      </div>
      <div className="opacity-0 group-hover:opacity-100 transition-opacity flex gap-1 items-start shrink-0">
        <Popover>
          <PopoverTrigger asChild>
            <button className="p-1 text-muted-foreground hover:text-foreground" data-testid={`message-react-btn-${msg.id}`}>
              <Smile className="h-3.5 w-3.5" />
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-1.5 flex gap-1">
            {EMOJIS.map((e) => (
              <button key={e} className="text-lg hover:scale-125 transition-transform" data-testid={`emoji-${e}-${msg.id}`}
                onClick={() => onReact(msg, e)}>{e}</button>
            ))}
          </PopoverContent>
        </Popover>
        <button className="p-1 text-muted-foreground hover:text-foreground" data-testid={`message-reply-btn-${msg.id}`}
          onClick={() => onReply(msg)}>
          <Reply className="h-3.5 w-3.5" />
        </button>
        {editable && (
          <button className="p-1 text-muted-foreground hover:text-foreground" data-testid={`message-edit-btn-${msg.id}`}
            onClick={() => onEdit(msg)}>
            <Pencil className="h-3.5 w-3.5" />
          </button>
        )}
        {(editable || isAdmin) && (
          <button className="p-1 text-destructive" data-testid={`message-delete-btn-${msg.id}`}
            onClick={() => onDelete(msg)}>
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </div>
  );
};

export default function ChatPage() {
  const { id: workspaceId } = useParams();
  const [params, setParams] = useSearchParams();
  const activeId = params.get("c");
  const qc = useQueryClient();
  const bottomRef = useRef(null);
  const fileRef = useRef(null);
  const [text, setText] = useState("");
  const [replyTo, setReplyTo] = useState(null);
  const [editing, setEditing] = useState(null);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [mentions, setMentions] = useState([]);
  const [dmOpen, setDmOpen] = useState(false);

  const { data: me } = useQuery({ queryKey: ["me"], queryFn: async () => (await api.get("/auth/me")).data });
  const { data: ws } = useQuery({
    queryKey: ["workspace", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}`)).data,
  });
  const { data: members = [] } = useQuery({
    queryKey: ["members", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/members`)).data,
  });
  const { data: conversations = [] } = useQuery({
    queryKey: ["conversations", workspaceId],
    queryFn: async () => (await api.get(`/workspaces/${workspaceId}/conversations`)).data,
    refetchInterval: 5000,
  });
  const { data: messages = [] } = useQuery({
    queryKey: ["messages", activeId],
    queryFn: async () => (await api.get(`/conversations/${activeId}/messages`)).data,
    enabled: !!activeId,
    refetchInterval: 4000,
  });

  const active = conversations.find((c) => c.id === activeId);
  const isAdmin = ["owner", "admin"].includes(ws?.my_role);
  const activeMembers = useMemo(
    () => members.filter((m) => m.status === "active" && m.profile_id !== me?.id),
    [members, me?.id]
  );

  useEffect(() => {
    if (activeId && messages.length) {
      api.post(`/conversations/${activeId}/read`).then(() =>
        qc.invalidateQueries({ queryKey: ["conversations", workspaceId] }));
    }
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [activeId, messages.length, qc, workspaceId]);

  const send = async () => {
    if (editing) {
      try {
        await api.put(`/messages/${editing.id}`, { text });
        setEditing(null);
        setText("");
        qc.invalidateQueries({ queryKey: ["messages", activeId] });
      } catch (e) {
        toast.error(apiError(e));
      }
      return;
    }
    if (!text.trim() && pendingFiles.length === 0) return;
    try {
      await api.post(`/conversations/${activeId}/messages`, {
        text, file_ids: pendingFiles.map((f) => f.id),
        reply_to_id: replyTo?.id || null, mentions,
      });
      setText(""); setReplyTo(null); setPendingFiles([]); setMentions([]);
      qc.invalidateQueries({ queryKey: ["messages", activeId] });
      qc.invalidateQueries({ queryKey: ["conversations", workspaceId] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const attachFile = async (f) => {
    const form = new FormData();
    form.append("file", f);
    form.append("entity_type", "conversation");
    form.append("entity_id", activeId);
    try {
      const { data } = await api.post(`/workspaces/${workspaceId}/files`, form);
      setPendingFiles((p) => [...p, data]);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const react = async (msg, emoji) => {
    await api.post(`/messages/${msg.id}/reactions`, { emoji });
    qc.invalidateQueries({ queryKey: ["messages", activeId] });
  };

  const deleteMsg = async (msg) => {
    try {
      await api.delete(`/messages/${msg.id}?scope=all`);
      qc.invalidateQueries({ queryKey: ["messages", activeId] });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const mention = (m) => {
    setText((t) => `${t}@${m.name} `);
    if (m.profile_id !== me?.id) setMentions((prev) => [...new Set([...prev, m.profile_id])]);
  };

  const startDm = async (m) => {
    try {
      const { data } = await api.post("/conversations/direct", { workspace_id: workspaceId, profile_id: m.profile_id });
      qc.invalidateQueries({ queryKey: ["conversations", workspaceId] });
      setDmOpen(false);
      setParams({ c: data.id });
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const groups = [
    { label: "Canal", items: conversations.filter((c) => c.type === "general") },
    { label: "Proyectos", items: conversations.filter((c) => c.type === "project") },
    { label: "Tareas", items: conversations.filter((c) => c.type === "task") },
    { label: "Directos", items: conversations.filter((c) => c.type === "direct") },
  ];

  return (
    <SpaceSection title="Chat" actions={
      <Button size="sm" variant="outline" data-testid="chat-new-dm-btn" onClick={() => setDmOpen(true)}>
        <MessageCirclePlus className="h-4 w-4 mr-1" /> Privado
      </Button>
    }>
      <div className="grid md:grid-cols-[280px,1fr] gap-4 h-[calc(100vh-260px)] min-h-[420px]">
        {/* Conversation list */}
        <div className={`border rounded-xl bg-card overflow-y-auto ${activeId ? "hidden md:block" : ""}`} data-testid="conversation-list">
          {groups.map((g) => g.items.length > 0 && (
            <div key={g.label}>
              <p className="px-3 pt-3 pb-1 text-[10px] uppercase tracking-[0.2em] font-semibold text-muted-foreground">{g.label}</p>
              {g.items.map((c) => (
                <button key={c.id} onClick={() => setParams({ c: c.id })}
                  data-testid={`conversation-item-${c.id}`}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-secondary/60 ${activeId === c.id ? "bg-accent" : ""}`}>
                  {c.type === "direct" ? (
                    <UserAvatar name={c.other_name} color={c.other_color} size="sm" />
                  ) : (
                    <span className="h-7 w-7 rounded-lg bg-secondary flex items-center justify-center text-sm shrink-0">{c.icon}</span>
                  )}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium truncate">{c.title}</p>
                    {c.last_message_preview && (
                      <p className="text-[11px] text-muted-foreground truncate">{c.last_message_preview}</p>
                    )}
                  </div>
                  {c.unread > 0 && (
                    <Badge className="h-5 min-w-5 px-1.5 text-[10px]" data-testid={`unread-badge-${c.id}`}>{c.unread}</Badge>
                  )}
                </button>
              ))}
            </div>
          ))}
        </div>

        {/* Thread */}
        <div className={`border rounded-xl bg-card flex flex-col overflow-hidden ${!activeId ? "hidden md:flex" : ""}`} data-testid="chat-thread">
          {!active ? (
            <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground">
              Selecciona una conversación
            </div>
          ) : (
            <>
              <div className="border-b px-4 py-2.5 flex items-center gap-2">
                <button className="md:hidden" onClick={() => setParams({})} data-testid="chat-back-btn">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <p className="font-heading font-semibold text-sm flex-1 truncate" data-testid="chat-thread-title">
                  {active.icon && <span className="mr-1.5">{active.icon}</span>}{active.title}
                </p>
                {active.archived && <Badge variant="secondary" className="text-[10px]">Solo lectura</Badge>}
              </div>
              <div className="flex-1 overflow-y-auto py-2" data-testid="messages-container">
                {messages.length === 0 && (
                  <p className="text-center text-xs text-muted-foreground py-8">Sé el primero en escribir 👋</p>
                )}
                {messages.map((m) => (
                  <MessageBubble key={m.id} msg={m} isMine={m.author_id === me?.id} isAdmin={isAdmin}
                    onReply={setReplyTo} onReact={react} onDelete={deleteMsg}
                    onEdit={(msg) => { setEditing(msg); setText(msg.text); }} />
                ))}
                <div ref={bottomRef} />
              </div>
              {!active.archived && (
                <div className="border-t p-3 space-y-2">
                  {replyTo && (
                    <div className="flex items-center gap-2 text-xs bg-secondary rounded-lg px-3 py-1.5">
                      <Reply className="h-3 w-3" />
                      <span className="flex-1 truncate">Respondiendo a <b>{replyTo.author_name}</b>: {replyTo.text?.slice(0, 60)}</span>
                      <button onClick={() => setReplyTo(null)} data-testid="cancel-reply-btn"><X className="h-3 w-3" /></button>
                    </div>
                  )}
                  {editing && (
                    <div className="flex items-center gap-2 text-xs bg-accent rounded-lg px-3 py-1.5">
                      <Pencil className="h-3 w-3" />
                      <span className="flex-1">Editando mensaje</span>
                      <button onClick={() => { setEditing(null); setText(""); }} data-testid="cancel-edit-btn"><X className="h-3 w-3" /></button>
                    </div>
                  )}
                  {pendingFiles.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {pendingFiles.map((f) => (
                        <span key={f.id} className="text-xs bg-secondary rounded-full px-2.5 py-1 flex items-center gap-1">
                          📎 {f.name}
                          <button onClick={() => setPendingFiles((p) => p.filter((x) => x.id !== f.id))}><X className="h-3 w-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Popover>
                      <PopoverTrigger asChild>
                        <button className="text-muted-foreground hover:text-foreground shrink-0" data-testid="mention-btn">
                          <AtSign className="h-4 w-4" />
                        </button>
                      </PopoverTrigger>
                      <PopoverContent className="w-48 p-1" align="start">
                        {isAdmin && (
                          <button className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary" data-testid="mention-all-btn"
                            onClick={() => { setText((t) => `${t}@Todos `); setMentions((p) => [...new Set([...p, "all"])]); }}>
                            @Todos
                          </button>
                        )}
                        {activeMembers.map((m) => (
                          <button key={m.id} className="w-full text-left text-sm px-2 py-1.5 rounded hover:bg-secondary"
                            data-testid={`mention-member-${m.id}`} onClick={() => mention(m)}>
                            @{m.name}
                          </button>
                        ))}
                      </PopoverContent>
                    </Popover>
                    <button className="text-muted-foreground hover:text-foreground shrink-0" data-testid="attach-file-btn"
                      onClick={() => fileRef.current?.click()}>
                      <Paperclip className="h-4 w-4" />
                    </button>
                    <input ref={fileRef} type="file" hidden data-testid="chat-file-input"
                      onChange={(e) => { if (e.target.files[0]) attachFile(e.target.files[0]); e.target.value = ""; }} />
                    <Input data-testid="chat-message-input" value={text} onChange={(e) => setText(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && !e.shiftKey && send()}
                      placeholder="Escribe un mensaje..." className="h-9" maxLength={4000} />
                    <Button size="sm" className="h-9 shrink-0" data-testid="chat-send-btn" onClick={send}>
                      <Send className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      <Dialog open={dmOpen} onOpenChange={setDmOpen}>
        <DialogContent className="sm:max-w-sm" data-testid="new-dm-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Chat privado</DialogTitle>
            <DialogDescription>Conversación directa entre dos personas.</DialogDescription>
          </DialogHeader>
          <div className="space-y-1">
            {activeMembers.map((m) => (
              <button key={m.id} onClick={() => startDm(m)} data-testid={`dm-member-${m.id}`}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-secondary text-left">
                <UserAvatar name={m.name} color={m.avatar_color} size="sm" />
                <span className="text-sm font-medium">{m.name}</span>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </SpaceSection>
  );
}
